import { Controller } from "@hotwired/stimulus";
import { useClickOutside } from "stimulus-use";

// Select simple avec icônes (non-multiple)
// Basé sur le template Preline pour les sélections simples
export default class extends Controller {
  static targets = [
    "hiddenSelect", // Le <select> original et caché
    "toggle", // Le bouton qui affiche la sélection actuelle
    "dropdown", // Le menu déroulant avec les options
    "option", // Chaque option cliquable dans le dropdown
    "checkIcon", // Les icônes de validation dans les options
  ];

  static values = {
    placeholder: String,
    toggleTemplate: String,
    allowBlank: Boolean,
  };

  connect() {
    // Utilise stimulus-use pour fermer le dropdown si on clique à l'extérieur
    if (this.hasDropdownTarget) {
      useClickOutside(this, { element: this.dropdownTarget });
    }

    // Rendre la sélection initiale
    this.renderInitialSelection();

    // Surveiller les changements sur le select caché (utile pour des mises à jour programmatiques)
    if (this.hasHiddenSelectTarget) {
      this.hiddenSelectTarget.addEventListener(
        "change",
        this.renderInitialSelection.bind(this)
      );

      // Intercepter l'événement invalid pour rediriger le focus vers le bouton visible
      this.hiddenSelectTarget.addEventListener(
        "invalid",
        this.handleInvalidValidation.bind(this)
      );
    }

    // Écouter l'événement personnalisé de clic sur label
    this.handleLabelClickBound = this.handleLabelClick.bind(this);
    window.addEventListener('select-component-label-click', this.handleLabelClickBound);
  }

  disconnect() {
    // Nettoyer l'écouteur d'événement global
    if (this.handleLabelClickBound) {
      window.removeEventListener('select-component-label-click', this.handleLabelClickBound);
    }
  }

  // Action appelée quand on clique en dehors du composant
  clickOutside(event) {
    if (this.dropdownTarget.classList.contains("hidden")) return;
    this.hideDropdown();
  }

  // Affiche ou cache le dropdown
  toggleDropdown(event) {
    event.preventDefault();
    this.dropdownTarget.classList.toggle("hidden");
    
    if (!this.dropdownTarget.classList.contains("hidden")) {
      this.toggleTarget.setAttribute("aria-expanded", "true");
    } else {
      this.toggleTarget.setAttribute("aria-expanded", "false");
    }
  }

  // Gère le clic sur le label associé
  handleLabelClick(event) {
    event.preventDefault();
    // Ouvrir le dropdown si fermé, sinon ne rien faire
    if (this.dropdownTarget.classList.contains("hidden")) {
      this.dropdownTarget.classList.remove("hidden");
      this.toggleTarget.setAttribute("aria-expanded", "true");
    }
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden");
    this.toggleTarget.setAttribute("aria-expanded", "false");
  }

  // Gère l'événement invalid sur le select caché pour rediriger le focus vers le bouton visible
  handleInvalidValidation(event) {
    event.preventDefault(); // Empêche l'affichage du message d'erreur natif

    // Focuser sur le bouton visible et ajouter une indication visuelle d'erreur
    if (this.hasToggleTarget) {
      this.toggleTarget.focus();
      this.toggleTarget.classList.add("border-red-500", "ring-2", "ring-red-200");
      
      // Optionnel : afficher le dropdown pour inviter l'utilisateur à choisir
      if (this.dropdownTarget.classList.contains("hidden")) {
        this.dropdownTarget.classList.remove("hidden");
        this.toggleTarget.setAttribute("aria-expanded", "true");
      }

      // Retirer l'indication d'erreur après un délai ou dès qu'une sélection est faite
      setTimeout(() => {
        this.toggleTarget.classList.remove("border-red-500", "ring-2", "ring-red-200");
      }, 3000);
    }
  }

  // Gère la sélection d'une option depuis le dropdown
  selectOption(event) {
    const selectedOptionDiv = event.currentTarget;
    const { value } = selectedOptionDiv.dataset;

    // Déselectionner toutes les options précédentes
    Array.from(this.hiddenSelectTarget.options).forEach(option => {
      option.selected = false;
    });

    // Sélectionner la nouvelle option
    const originalOption = this.findOriginalOption(value);
    if (originalOption) {
      originalOption.selected = true;
    }

    // Mettre à jour l'affichage du toggle
    this.updateToggleDisplay(selectedOptionDiv);

    // Mettre à jour l'état visuel des options
    this.updateOptionsState();

    // Retirer les classes d'erreur de validation si elles sont présentes
    if (this.hasToggleTarget) {
      this.toggleTarget.classList.remove("border-red-500", "ring-2", "ring-red-200");
    }

    // Déclencher l'événement change sur le select caché pour l'auto-submit
    if (this.hasHiddenSelectTarget) {
      this.hiddenSelectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.hideDropdown();
  }

  // Met à jour l'affichage du bouton toggle avec la sélection
  updateToggleDisplay(optionElement) {
    const title = optionElement.dataset.title;
    const iconBase64 = optionElement.dataset.icon;
    
    // Trouver les éléments dans le toggle
    const titleElement = this.toggleTarget.querySelector('[data-title]');
    const iconElement = this.toggleTarget.querySelector('[data-icon]');

    // Mettre à jour le titre
    if (titleElement) {
      titleElement.textContent = title;
    }

    // Mettre à jour l'icône
    if (iconElement && iconBase64) {
      const decodedIcon = atob(iconBase64);
      iconElement.innerHTML = decodedIcon;
    }
  }

  // Met à jour l'état visuel des options (checked/unchecked)
  updateOptionsState() {
    const selectedValue = this.hiddenSelectTarget.value;
    
    this.optionTargets.forEach((optionEl) => {
      const optionValue = optionEl.dataset.value;
      const isSelected = selectedValue === optionValue;
      
      // Trouver l'icône checkIcon dans cette option spécifique
      const checkIcon = this.checkIconTargets.find(icon => 
        optionEl.contains(icon)
      );
      const titleElement = optionEl.querySelector('[data-title]');
      
      if (isSelected) {
        // Afficher l'icône et mettre en gras le texte pour l'option sélectionnée
        if (checkIcon) {
          checkIcon.classList.remove("hidden");
        }
        if (titleElement) {
          titleElement.classList.add("font-semibold");
        }
      } else {
        // Masquer l'icône et enlever le gras pour les options non sélectionnées
        if (checkIcon) {
          checkIcon.classList.add("hidden");
        }
        if (titleElement) {
          titleElement.classList.remove("font-semibold");
        }
      }
    });
  }

  // ========== Méthodes privées de rendu et d'aide ==========

  // Au chargement, affiche la sélection initiale
  renderInitialSelection() {
    if (!this.hasHiddenSelectTarget) return;

    const selectedOption = Array.from(this.hiddenSelectTarget.options).find(option => option.selected);
    
    if (selectedOption) {
      // Trouver l'option correspondante dans le dropdown
      const dropdownOption = this.findDropdownOption(selectedOption.value);
      if (dropdownOption) {
        this.updateToggleDisplay(dropdownOption);
      }
    } else if (!this.allowBlankValue && this.hiddenSelectTarget.options.length > 0) {
      // Si allow_blank est false et qu'aucune option n'est sélectionnée, sélectionner la première
      const firstOption = this.hiddenSelectTarget.options[0];
      if (firstOption && firstOption.value !== "") {
        firstOption.selected = true;
        const dropdownOption = this.findDropdownOption(firstOption.value);
        if (dropdownOption) {
          this.updateToggleDisplay(dropdownOption);
        }
      }
    } else {
      // allow_blank est true ou aucune option disponible, afficher le placeholder
      const titleElement = this.toggleTarget.querySelector('[data-title]');
      const iconElement = this.toggleTarget.querySelector('[data-icon]');
      
      if (titleElement) {
        titleElement.textContent = this.placeholderValue;
      }
      if (iconElement) {
        iconElement.innerHTML = "";
      }
    }

    this.updateOptionsState();
  }

  // Retrouve une <option> originale dans le <select> caché par sa valeur
  findOriginalOption(value) {
    if (!this.hasHiddenSelectTarget) return null;
    return Array.from(this.hiddenSelectTarget.options).find(
      (o) => o.value === value
    );
  }

  // Retrouve un <div> d'option dans le dropdown par sa valeur
  findDropdownOption(value) {
    return this.optionTargets.find((el) => el.dataset.value === value);
  }
}
